import {
  Button,
  Checkbox,
  FormControlLabel,
  Theme,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { FaBullhorn } from 'react-icons/fa';
import { makeStyles } from 'tss-react/mui';
import { WhatsNewItem, WhatsNewType } from '../whats-new-types';
import WhatsNewItemRenderer from './WhatsNewItemRenderer';

const WhatsNew: FC<React.PropsWithChildren<WhatsNewProps>> = ({
  items,
  onClose,
  dismissable,
}) => {
  const { classes } = useStyles();
  const [dismiss, setDismiss] = useState(true);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDismiss(event.target.checked);
  };

  const handleDismissClose = useCallback(() => {
    onClose && onClose(dismiss);
  }, [dismiss, onClose]);

  return (
    <div className={classes.whatsnew}>
      <div className={classes.header}>
        <FaBullhorn />
        <Typography variant='body1'>What's New</Typography>
      </div>
      <div>
        <ul className={classes.list}>
          {items.map(({ id, type, description, links }) => (
            <li
              key={id}
              className={
                type === WhatsNewType.NewUIVersionAvailable
                  ? classes.noBullet
                  : ''
              }>
              <WhatsNewItemRenderer
                type={type}
                description={description}
                links={links}
              />
            </li>
          ))}
        </ul>
      </div>
      <div className={classes.footer}>
        {dismissable && (
          <>
            <FormControlLabel
              color='inherit'
              control={
                <Checkbox
                  size='small'
                  onChange={handleChange}
                  checked={dismiss}
                  sx={{
                    color: 'inherit',
                    '&.Mui-checked': {
                      color: 'inherit',
                    },
                  }}
                />
              }
              label="Don't show these items again"
            />
            <Button onClick={handleDismissClose} sx={{ color: 'inherit' }}>
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  whatsnew: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
    position: 'relative',
    gap: theme.spacing(4),
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    gap: theme.spacing(1),
  },
  footer: {
    whiteSpace: 'nowrap',
  },
  list: {
    paddingInline: theme.spacing(2),
    listStyle: 'inside',
    listStylePosition: 'outside',
    [theme.breakpoints.up('lg')]: {
      paddingInline: 0,
    },
  },
  noBullet: {
    listStyle: 'none',
    position: 'relative',
    left: `-${theme.spacing(1)}`,
  },
}));

export interface WhatsNewProps {
  items: WhatsNewItem[];
  dismissable: boolean;
  onClose: (dismiss: boolean) => void;
}

export default WhatsNew;
